import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Gift, Mail, CreditCard } from "lucide-react";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const PRESETS = [100, 250, 350, 495];
const inputCls = "w-full bg-white/80 border border-[#1A1A1A]/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]/40 transition-colors placeholder:text-[#9A9A94]";

export default function GiftCards() {
  const [preset, setPreset] = useState(250);
  const [custom, setCustom] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [toOther, setToOther] = useState(true);
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", recipient_email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sp] = useSearchParams();
  const toastOnce = useRef(false);

  useEffect(() => {
    const v = sp.get("bought");
    if (toastOnce.current || !v) return;
    toastOnce.current = true;
    setTimeout(() => {
      if (v === "success") toast.success("Payment received — the gift card is on its way by email.");
      else toast.info("Checkout cancelled — no charge was made.");
      window.history.replaceState({}, "", window.location.pathname);
    }, 50);
  }, [sp]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const finalAmount = isCustom ? (parseFloat(custom) || 0) : preset;
  const valid = finalAmount >= 25 && finalAmount <= 5000 && form.buyer_name && form.buyer_email && (!toOther || (form.recipient_name && form.recipient_email));

  const buy = async () => {
    setLoading(true);
    try {
      const payload = { amount: finalAmount, buyer_name: form.buyer_name, buyer_email: form.buyer_email, origin_url: window.location.origin };
      if (toOther) {
        payload.recipient_name = form.recipient_name;
        payload.recipient_email = form.recipient_email;
        payload.message = form.message;
      }
      const r = await api.post("/gift-cards/checkout", payload);
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setLoading(false);
    }
  };

  return (
    <main data-testid="gift-cards-page" className="pt-32 pb-28 overflow-hidden">
      <Seo title="Gift Cards — Bahare | Photography Gift Cards in Los Angeles"
        description="Give a photography session — gift cards from $25, delivered by email with a unique code." />
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <Reveal className="text-center">
          <motion.span initial={{ rotate: -6, scale: 0.8, opacity: 0 }} animate={{ rotate: -3, scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-block bg-[#E4572E] text-[#F9F9F8] text-[10px] uppercase tracking-[0.25em] px-5 py-2 rounded-full mb-8">
            Delivered by email · Instantly
          </motion.span>
          <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[1.02]">
            Give memories,<br /><span className="italic text-[#E4572E]">not things.</span>
          </h1>
          <p className="mt-6 text-sm text-[#4A4A4A] max-w-md mx-auto leading-relaxed">
            A photography gift card toward any session — maternity, newborn, family, portraits. Beautifully delivered by email with a unique code.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-8 mt-20 items-start">
          <Reveal className="lg:col-span-5">
            <div className="rounded-[2rem] bg-[#F5EED8] p-8 md:p-10">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-8">How it works</p>
              {[
                [<Gift key="g" size={16} strokeWidth={1.5} />, "Choose an amount", "Any value from $25 — it goes toward any session or package."],
                [<CreditCard key="c" size={16} strokeWidth={1.5} />, "Pay securely online", "Card, Apple Pay and more — takes less than a minute."],
                [<Mail key="m" size={16} strokeWidth={1.5} />, "It lands in their inbox", "A beautiful gift card with your note and a unique code — ready to print or forward."],
              ].map(([icon, t, d], i) => (
                <div key={i} className="flex gap-4 mb-8 last:mb-0">
                  <span className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center shrink-0">{icon}</span>
                  <div>
                    <p className="font-serif text-xl mb-1">{t}</p>
                    <p className="text-sm text-[#4A4A4A] leading-relaxed">{d}</p>
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-[#7A7A7A] leading-relaxed mt-8 border-t border-[#1A1A1A]/10 pt-6">
                To redeem, the lucky one simply mentions their code when booking. Gift cards never expire.
              </p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <div className="rounded-[2rem] bg-[#E7E3F2] p-8 md:p-10" data-testid="gift-card-form">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-4">1 · Amount</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {PRESETS.map((a) => (
                  <button key={a} onClick={() => { setPreset(a); setIsCustom(false); }}
                    data-testid={`gift-amount-${a}`}
                    className={`text-sm px-6 py-3 rounded-full border transition-colors duration-300 ${!isCustom && preset === a ? "bg-[#1A1A1A] text-[#F9F9F8] border-[#1A1A1A]" : "bg-white/70 border-transparent hover:border-[#1A1A1A]"}`}>
                    ${a}
                  </button>
                ))}
                <button onClick={() => setIsCustom(true)} data-testid="gift-amount-custom"
                  className={`text-sm px-6 py-3 rounded-full border transition-colors duration-300 ${isCustom ? "bg-[#1A1A1A] text-[#F9F9F8] border-[#1A1A1A]" : "bg-white/70 border-transparent hover:border-[#1A1A1A]"}`}>
                  Custom
                </button>
              </div>
              {isCustom && (
                <input type="number" min="25" max="5000" value={custom} onChange={(e) => setCustom(e.target.value)}
                  placeholder="Amount in USD (25 – 5,000)" data-testid="gift-custom-amount" className={`${inputCls} mb-3`} />
              )}

              <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-4 mt-8">2 · Your details</p>
              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                <input placeholder="Your name *" value={form.buyer_name} onChange={set("buyer_name")} data-testid="gift-buyer-name" className={inputCls} />
                <input placeholder="Your email *" type="email" value={form.buyer_email} onChange={set("buyer_email")} data-testid="gift-buyer-email" className={inputCls} />
              </div>

              <label className="flex items-center gap-3 text-sm text-[#4A4A4A] cursor-pointer mb-4" data-testid="gift-to-other-wrap">
                <input type="checkbox" checked={toOther} onChange={(e) => setToOther(e.target.checked)}
                  className="accent-[#1A1A1A] w-4 h-4" data-testid="gift-to-other" />
                It's a gift — email it to someone special
              </label>
              {toOther && (
                <div className="space-y-3 mb-6" data-testid="gift-recipient-fields">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input placeholder="Their name *" value={form.recipient_name} onChange={set("recipient_name")} data-testid="gift-recipient-name" className={inputCls} />
                    <input placeholder="Their email *" type="email" value={form.recipient_email} onChange={set("recipient_email")} data-testid="gift-recipient-email" className={inputCls} />
                  </div>
                  <textarea placeholder="Your note to them (optional) — e.g. Can't wait to see you as a mom-to-be!" rows={2}
                    value={form.message} onChange={set("message")} data-testid="gift-message" className={`${inputCls} resize-none`} />
                </div>
              )}

              <button onClick={buy} disabled={loading || !valid} data-testid="gift-pay"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#F9F9F8] text-[11px] uppercase tracking-[0.2em] py-4 rounded-full hover:bg-[#E4572E] transition-colors disabled:opacity-50">
                {loading ? "Redirecting…" : `Purchase Gift Card${finalAmount >= 25 ? ` — $${finalAmount}` : ""}`} <ArrowRight size={13} strokeWidth={1.5} />
              </button>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] text-center mt-4">Secure online payment · Card, Apple Pay & more</p>
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
